/**
 * Command results (plan §6 M2-01): every Career command returns a `CmdResult` and never throws for a normal
 * refusal. Refusals carry a typed code and a player-facing English message; successes carry the new value and
 * the effects the career runtime turns into notices, stingers and comms (`SEV.career`).
 *
 * FROZEN after wave 1 (plan §1.2): later tasks may add optional members only.
 */
import type { ContractType, SimFactionId, StationId } from '../data/m2Schemas'
import type { Collections } from './types'

export const CMD_ERROR_CODES = [
  'insufficientFunds',
  'cargoFull',
  'notInCargo',
  'tooLarge',
  'powerBudget',
  'slotMismatch',
  'notSoldHere',
  'notBoughtHere',
  'serviceUnavailable',
  'contractUnavailable',
  'contractRequirement',
  'nothingToRepair',
  'torpedoesFull',
  'notInLocker',
  'invalidAmount',
  'leaseOwned',
  // Session and saves (M2-12, M2-18, M2-22).
  'notReady',
  'emptySlot',
  'corrupt',
  'migrationFailed',
  'future',
  'invalidImport',
  'storageError',
  // M4 (plan I3): the strategic market's price moved past the 2% tolerance before the trade executed.
  'priceMoved',
] as const
export type CmdErrorCode = (typeof CMD_ERROR_CODES)[number]

export interface CommandError {
  code: CmdErrorCode
  /** Player-facing English, e.g. "Not enough credits: 12,400 needed, 9,850 available." */
  message: string
  detail?: Record<string, string | number>
}

/** Side effects of a successful command, for notices, audio and the career runtime. */
export type CareerEffect =
  | { kind: 'leasePaid'; amount: number; principal: number }
  | { kind: 'paymentMissed'; missed: number; arrears: number; lateFee: number }
  | { kind: 'collectionsChanged'; from: Collections; to: Collections }
  | { kind: 'repoTriggered' }
  | { kind: 'ownership'; gameTime: number }
  /** `type` and `station` (M7 S33): the job handed in and its issuer, for the desk's thank-you (Hale, clear raiders). */
  | { kind: 'contractCompleted'; contractId: string; reward: number; standing: number; type: ContractType; station: StationId }
  | { kind: 'contractFailed'; contractId: string; standing: number }
  | { kind: 'standingChanged'; faction: SimFactionId; delta: number; value: number }
  | { kind: 'dataCoreLogged'; id: string }
  | { kind: 'debtIncurred'; amount: number; debt: number }
  | { kind: 'premiumCharged'; amount: number }
  /** A death's rebuild fee (plus the repo fee when a repo crew was active). */
  | { kind: 'deathFee'; amount: number; repoFee: number; addedToPrincipal: boolean }
  /** A command's own result line (PF-5: a data core read, a pattern studied), shown as a station notice. */
  | { kind: 'resultLine'; text: string }

export type CareerEffectKind = CareerEffect['kind']

export interface CmdSuccess<T> {
  ok: true
  value: T
  effects: CareerEffect[]
}

export interface CmdFailure {
  ok: false
  error: CommandError
}

export type CmdResult<T> = CmdSuccess<T> | CmdFailure

export function ok<T>(value: T, effects: CareerEffect[] = []): CmdSuccess<T> {
  return { ok: true, value, effects }
}

export function fail(code: CmdErrorCode, message: string, detail?: Record<string, string | number>): CmdFailure {
  return detail === undefined ? { ok: false, error: { code, message } } : { ok: false, error: { code, message, detail } }
}

/** Whole credits with thousands separators: 12400 → "12,400" (rounded; for messages and the UI). */
export function formatCredits(amount: number): string {
  const n = Math.round(amount)
  const digits = String(Math.abs(n))
  let out = ''
  for (let i = 0; i < digits.length; i++) {
    if (i > 0 && (digits.length - i) % 3 === 0) out += ','
    out += digits[i]
  }
  return n < 0 ? `-${out}` : out
}

/** Tonnes to one decimal: 12.345 → "12.3". */
export function formatTonnes(tonnes: number): string {
  return (Math.round(tonnes * 10) / 10).toFixed(1)
}

/** The standard funds refusal. */
export function insufficientFunds(needed: number, available: number): CmdFailure {
  return fail('insufficientFunds', `Not enough credits: ${formatCredits(needed)} needed, ${formatCredits(available)} available.`, {
    needed,
    available,
  })
}

/** The standard hold refusal. */
export function cargoFull(neededT: number, freeT: number): CmdFailure {
  return fail('cargoFull', `Not enough cargo space: ${formatTonnes(neededT)} t needed, ${formatTonnes(freeT)} t free.`, {
    needed: neededT,
    free: freeT,
  })
}

/** The standard bad-amount refusal (non-finite, zero or negative quantities). */
export function invalidAmount(what: string, amount: number): CmdFailure {
  return fail('invalidAmount', `Invalid ${what}: ${String(amount)}.`, { amount })
}
